import type { BoundingBox } from 'types/map';
import type { PolygonType } from 'types/Polygon';

/**
 * Transform the polyon bounds into an ingestible format for our ElasticSearch service.
 *
 * @param polygon The coordinates of the points that make up the polygon
 * @returns A string representation of the polygon
 */
export const getBoundingBoxesFromPolygon = (polygon: PolygonType): BoundingBox => {
  if (!polygon || polygon.length < 2) {
    return '';
  }

  const boundingBox: string[] = [];
  polygon.forEach((coord: any) => {
    boundingBox.push(`${coord.lng} ${coord.lat}`);
  });

  return boundingBox.join(', ');
};

/**
 * Transform the polyon bounds into an ingestible format for our ElasticSearch service.
 *
 * @param polygon The coordinates of the points that make up the polygon
 * @returns A string representation of the polygon
 */
export const getPolygonFromBoundingBox = (boundingBox: BoundingBox): PolygonType => {
  if (!boundingBox || boundingBox.length < 2) {
    return [];
  }

  return boundingBox.split(',').map((coord) => {
    const [lng, lat] = coord.trim().split(' ').map(Number);

    return { lat, lng };
  });
};

export const getCenterOfPolygon = (polygon: PolygonType) => {
  if (!polygon || polygon.length === 0) {
    return {};
  }

  const latitudes = polygon.map((point) => point.lat);
  const longitudes = polygon.map((point) => point.lng);

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  return { lat: centerLat, lng: centerLng };
};

export const getLatLngBoundsFromPolygon = (
  polygon: PolygonType,
): google.maps.LatLngBoundsLiteral | null => {
  if (!polygon || polygon.length === 0) {
    return null;
  }

  const latitudes = polygon.map((point) => point.lat);
  const longitudes = polygon.map((point) => point.lng);

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  return {
    north: maxLat,
    south: minLat,
    east: maxLng,
    west: minLng,
  };
};
